import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";
import { getCurrentMileageRate } from "../utils/mileageTracking";

const currentYear = new Date().getFullYear();
const currentMileageRate = getCurrentMileageRate(currentYear);

export default function FAQ() {
  const faqs = [
    {
      question: "Do I need to pay quarterly taxes?",
      answer: "If you expect to owe more than $1,000 in taxes and your income isn't subject to withholding, you're required to pay quarterly estimated taxes. Free1099Calc can help determine how much to send each quarter. The IRS requires payments in April, June, September, and January to avoid underpayment penalties."
    },
    {
      question: "How much should I set aside from each payment?",
      answer: "Most independent workers save 25–30% of each payment to cover federal income and self-employment tax. High earners (over $100k) may want to save 30-35% to avoid underpayment penalties. This accounts for federal income tax (10-37% depending on bracket), self-employment tax (15.3%), and state income tax where applicable."
    },
    {
      question: "What is self-employment tax?",
      answer: "This tax covers Social Security (12.4%) and Medicare (2.9%), totaling 15.3%. It applies to net earnings over $400 from 1099 work. Traditional employees pay half (7.65%) while employers pay the other half, but as a 1099 worker you pay both halves. The good news: you can deduct half of this on your personal tax return, reducing your adjusted gross income."
    },
    {
      question: "Can I deduct expenses like software and my phone bill?",
      answer: `Yes — common deductions include internet, phone (business use portion), office supplies, software subscriptions, mileage (${currentMileageRate.toFixed(1)}¢ per mile in ${currentYear}), and part of your home if used exclusively and regularly for business. Keep detailed records and receipts. The home office deduction requires exclusive use of a designated space.`
    },
    {
      question: "What if I have both W-2 and 1099 income?",
      answer: "Enter both amounts into the calculator. It will combine them to estimate total income and ensure accurate tax planning for mixed income types. Your W-2 withholding will count toward your total tax liability, but you'll still need to make quarterly estimated payments on your 1099 income if it's substantial."
    },
    {
      question: "How often should I use Free1099Calc?",
      answer: "We recommend updating your inputs every quarter or whenever your income changes significantly. This helps you adjust estimated tax payments and avoid surprises at filing time. Also revisit your assumptions when tax brackets or rates change (usually annually)."
    },
    {
      question: "Is this calculator accurate for my state?",
      answer: "The calculator provides federal tax estimates and general state tax guidance. State tax rules vary significantly — some states have no income tax (like Texas, Florida, Washington), while others have progressive brackets similar to federal taxes. Always check your state's Department of Revenue website for specific rules and rates."
    },
    {
      question: "Is Free1099Calc really free?",
      answer: "Yes, completely free with no hidden costs, account requirements, or paywalls. We're supported by non-intrusive ads and believe quality tax tools should be accessible to all freelancers and contractors. All calculations happen in your browser — we don't store your financial data."
    }
  ];

  return (
    <>
      <Helmet>
        <title>1099 Tax FAQ - Common Questions About Freelancer & Contractor Taxes</title>
        <meta name="description" content="Answers to common 1099 tax questions: quarterly payments, self-employment tax, deductions, estimated taxes, and tax planning for freelancers and independent contractors." />
        <meta name="keywords" content="1099 tax FAQ, quarterly taxes, self-employment tax, freelancer tax questions, contractor tax help, estimated tax payments, business deductions, 1099 form questions" />
        <link rel="canonical" href="https://free1099calc.com/faq" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="1099 Tax FAQ - Common Questions About Freelancer Taxes" />
        <meta property="og:description" content="Get answers to common 1099 tax questions about quarterly payments, self-employment tax, deductions, and tax planning for independent contractors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/faq" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="1099 Tax FAQ - Common Questions for Freelancers" />
        <meta name="twitter:description" content="Answers to quarterly taxes, self-employment tax, deductions, and more for 1099 workers." />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-5 py-2.5 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <span className="text-white text-sm font-bold">❓ HELP CENTER</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Quick answers to common questions about 1099 taxes, quarterly payments, and deductions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Calculate My Taxes →
              </Link>
              <Link
                to="/about"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">

      {/* Expense Tracker Banner */}
      <div className="mb-8">
        <ExpenseTrackerBanner compact />
      </div>

      {/* FAQ Items */}
      <div className="space-y-6">
        {faqs.map((faq, index) => (
          <div key={index} className="rounded-3xl border-2 border-teal-200 bg-white p-8 md:p-10 hover:shadow-xl transition-shadow">
            <h2 className="text-2xl md:text-3xl font-black mb-4 text-gray-900">{faq.question}</h2>
            <p className="text-gray-700 leading-relaxed text-lg">{faq.answer}</p>
          </div>
        ))}
      </div>

      {/* Additional Help Section */}
      <section className="mt-10 rounded-2xl border bg-gradient-to-br from-primary-50 to-accent-50 p-6 md:p-8">
        <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
        <p className="text-slate-700 mb-4">
          If you didn't find what you're looking for, we have additional resources to help:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-1">Use the Tax Calculator</h3>
            <p className="text-sm text-slate-600">Get personalized tax estimates based on your income and expenses</p>
          </Link>
          <Link to="/about" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-1">Read the About Page</h3>
            <p className="text-sm text-slate-600">Comprehensive guide to 1099 taxes, deductions, and quarterly payments</p>
          </Link>
          <Link to="/blog/external" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-1">Official IRS Resources</h3>
            <p className="text-sm text-slate-600">Links to authoritative tax guidance from the IRS and other agencies</p>
          </Link>
          <Link to="/contact" className="block rounded-lg border border-primary-200 bg-white p-4 hover:shadow-md transition-shadow">
            <h3 className="font-semibold text-primary-700 mb-1">Contact Us</h3>
            <p className="text-sm text-slate-600">Send us your question and we'll get back to you</p>
          </Link>
        </div>
      </section>

      <RecentPosts />
      </div>
    </>
  );
}
